/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { decode, encode } from 'cbor-web';
import { MAX_REQUEST_N } from 'rsocket-core';
import { RSocketBaseService } from '../RSocketBaseService';
import { RSocketChannelClientInterface } from '../RSocketObject';

// REQ is the request type for the updateStreamRequest method
// T is the local cache type, RESP is the response type
// Local cache is used to store the stream data and gets passed to listeners
// Response type is the type of the data received from the stream
export abstract class RSocketChannelBaseService<REQ, T, RESP> extends RSocketBaseService<T, RESP> {
  protected stream = undefined as RSocketChannelClientInterface | undefined;

  // This method is used to send the next request on the channel
  async updateStreamRequest(request: REQ): Promise<void> {
    this.request = request;
    // Send the updated request to the backend
    this.stream?.onNext(
      {
        data: encode(request),
        metadata: this.buildMetadata(),
      },
      false
    );
  }

  protected async createStream(): Promise<RSocketChannelClientInterface | undefined> {
    // Get the socket connection based on the sanctioning body and connection type (this was created in the createSocket method)
    const socket = RSocketChannelBaseService.socket.get(this.sanctioningBody)?.get(this.connnectionType);
    if (socket) {
      console.log('Channel Created:', { route: this.route, streamRequestData: this.request });
      const requester = socket.requestChannel(
        {
          data: undefined, // This is the initial request to acknowledge the connection
          metadata: this.buildMetadata(),
        },
        MAX_REQUEST_N,
        false,
        {
          onError: (e) => {
            console.log(`Error for channel ${this.route}: ${e}`);
            requester.cancel();
            this.reconnectToStream();
          },
          // This method is called whenever a message is received from the backend
          onNext: (payload, isComplete) => {
            const data: RESP = decode(payload.data as Buffer);
            // Send the data to the onDataCallback method (this is overridden in the child class)
            this.onDataCallback(data);

            if (isComplete) {
              console.log(`Channel ${this.route} recieved is complete`);
            }
          },
          onComplete: () => {
            // If the stream sends an error or complete message, reconnect to the stream
            console.log(`Channel ${this.route} complete`);
            this.reconnectToStream(true);
          },
          onExtension: () => {},
          request: (n) => {
            // This is called by the backend to let the frontend know the frontend can send requests
          },
          // This is called when the stream is stopped/cancelled
          cancel: () => {},
        }
      );
      // Send the actual user requested initial request to the channel
      requester.onNext(
        {
          data: encode(this.request),
          metadata: this.buildMetadata(),
        },
        false
      );
      return requester;
    } else {
      this.reconnectToStream();
    }
  }
}
